/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Taskboard/SignalR/TaskboardSignalRHub" />
import { IHubHostInformation, SingleConnectionSignalRHub } from "VSS/Features/SignalR/Hub";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import {
    ITaskboardWorkItemChangedPayload,
    ITaskboardWorkItemColumnChangedPayload,
    TaskboardChangedDefaultCallback,
    TaskboardWorkItemChangedCallback,
    TaskboardWorkItemColumnChangedCallback
} from "Wit/SprintsHub/Common/Contracts/Interfaces";

export interface ITaskboardSignalROptions {
    hostInformation: IHubHostInformation;
    historyService: IVssHistoryService;
    pageContext: IVssPageContext;
    useSignalRAppPoolDefaultValue?: boolean;
}

const UserInactivityTimeoutMs = 60 * 60 * 1000; // 1 hour
const TimeoutDelayMs = 30000;

interface ITaskboardHandlers {
    onWorkItemsUpdated: TaskboardWorkItemChangedCallback;
    onWorkItemColumnUpdated: TaskboardWorkItemColumnChangedCallback;
    onTaskboardColumnOptionsChanged: TaskboardChangedDefaultCallback;
    onTaskboardCardSettingsChanged: TaskboardChangedDefaultCallback;
    onTeamSettingsChanged: TaskboardChangedDefaultCallback;
}

interface ITaskboardHubClient
    extends Pick<ITaskboardHandlers, "onTaskboardColumnOptionsChanged" | "onTaskboardCardSettingsChanged" | "onTeamSettingsChanged"> {
    onWorkItemUpdated: (payload: ITaskboardWorkItemChangedPayload) => void;
    onWorkItemColumnUpdated: (payload: ITaskboardWorkItemColumnChangedPayload) => void;
}

interface ITaskboardHubServer {
    watchTaskboard: (teamId: string) => Promise<void>;
    unwatchTaskboard: (teamId: string) => Promise<void>;
}

function getProxyToRegister(
    baseProxy: ISignalRHubProxy<ITaskboardHubClient, ITaskboardHubServer>
): ISignalRHubProxy<ITaskboardHubClient, ITaskboardHubServer> {
    baseProxy.server = {
        watchTaskboard: (teamId: string) => baseProxy.invoke.apply(baseProxy, ["WatchTaskboard", teamId]),
        unwatchTaskboard: (teamId: string) => baseProxy.invoke.apply(baseProxy, ["UnwatchTaskboard", teamId])
    };

    return baseProxy;
}

export class TaskboardSignalRHub extends SingleConnectionSignalRHub<ITaskboardHubClient, ITaskboardHubServer> {
    private static Name = "taskboardHub";

    private timeoutHandle: number | null = null;
    private handlers: ITaskboardHandlers | null = null;
    private teamId: string | null = null;

    private get hub() {
        return this.getHub(TaskboardSignalRHub.Name);
    }

    constructor(options: ITaskboardSignalROptions) {
        super({
            name: TaskboardSignalRHub.Name,
            userInactivityTimeoutMs: UserInactivityTimeoutMs,
            proxyToRegister: getProxyToRegister,
            removeApiPath: true,
            ...options
        });
        this.initializeProxyClient(TaskboardSignalRHub.Name, proxy => this._initializeClient(proxy));
    }

    public async watch(
        teamId: string,
        onWorkItemsUpdated: TaskboardWorkItemChangedCallback,
        onWorkItemColumnUpdated: TaskboardWorkItemColumnChangedCallback,
        onTaskboardColumnOptionsChanged: TaskboardChangedDefaultCallback,
        onTaskboardCardSettingsChanged: TaskboardChangedDefaultCallback,
        onTeamSettingsChanged: TaskboardChangedDefaultCallback
    ) {
        if (this.teamId && this.teamId !== teamId) {
            await this.endWatch(this.teamId);
        }

        this.teamId = teamId;
        this.handlers = {
            onWorkItemsUpdated,
            onWorkItemColumnUpdated,
            onTaskboardColumnOptionsChanged,
            onTaskboardCardSettingsChanged,
            onTeamSettingsChanged
        };

        try {
            await this.start();
            await this.hub?.server.watchTaskboard(teamId);
        } catch (e: unknown) {
            this._stopOnTimeout();
            this.handlers = null;
            throw new Error("Not initialized");
        }
    }

    public async endWatch(boardId: string) {
        if (boardId !== this.teamId) {
            return;
        }

        this._clearTimeout();
        this.teamId = null;
        this.handlers = null;

        await this.hub?.server.unwatchTaskboard(boardId);
    }

    /**
     * Initialize the hub.
     */
    private _initializeClient(proxy: ISignalRHubProxy<ITaskboardHubClient, ITaskboardHubServer>) {
        proxy.client.onWorkItemUpdated = (payload: ITaskboardWorkItemChangedPayload) => this.handlers?.onWorkItemsUpdated([payload]);
        proxy.client.onWorkItemColumnUpdated = (payload: ITaskboardWorkItemColumnChangedPayload) => this.handlers?.onWorkItemColumnUpdated([payload]);
        proxy.client.onTaskboardColumnOptionsChanged = () => this.handlers?.onTaskboardColumnOptionsChanged();
        proxy.client.onTaskboardCardSettingsChanged = () => this.handlers?.onTaskboardCardSettingsChanged();
        proxy.client.onTeamSettingsChanged = () => this.handlers?.onTeamSettingsChanged();
    }

    private _stopOnTimeout() {
        this.timeoutHandle = setTimeout(() => this.stop(), TimeoutDelayMs);
    }

    private _clearTimeout(): void {
        if (this.timeoutHandle) {
            clearTimeout(this.timeoutHandle);
            this.timeoutHandle = null;
        }
    }
}
